/**
 * POS Cash Session - Cash register management
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, ArrowLeft, Clock, DollarSign, CreditCard,
  TrendingUp, Check, X, AlertTriangle, History,
  Banknote, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Open session modal
const OpenSessionModal = ({ onClose, onOpen }) => {
  const [initialCash, setInitialCash] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    if (!initialCash || parseFloat(initialCash) < 0) {
      toast.error('Ingresa el efectivo inicial');
      return;
    }

    setLoading(true);
    try {
      await onOpen({
        initial_cash: parseFloat(initialCash),
        notes: notes || undefined,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Abrir Caja</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="text-center p-4 bg-primary/10 rounded-xl">
            <Wallet className="w-12 h-12 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Ingresa el efectivo con el que inicias el turno
            </p>
          </div>

          <div>
            <Label>Efectivo Inicial *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={initialCash}
              onChange={(e) => setInitialCash(e.target.value)}
              placeholder="0.00"
              className="text-xl text-center font-bold"
              autoFocus
            />
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones al abrir caja"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleOpen} className="flex-1" disabled={loading}>
              {loading ? 'Abriendo...' : 'Abrir Caja'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Close session modal
const CloseSessionModal = ({ session, onClose, onCloseSession }) => {
  const [finalCash, setFinalCash] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const expectedCash = session?.summary?.expected_cash || session?.initial_cash || 0;
  const difference = parseFloat(finalCash || 0) - expectedCash;

  const handleClose = async () => {
    if (!finalCash) {
      toast.error('Ingresa el efectivo final');
      return;
    }

    setLoading(true);
    try {
      await onCloseSession({
        final_cash: parseFloat(finalCash),
        notes: notes || undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card rounded-2xl border border-border shadow-xl"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Cerrar Caja</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Expected summary */}
          <div className="bg-secondary rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Efectivo inicial</span>
              <span>${(session?.initial_cash || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Ventas en efectivo</span>
              <span className="text-green-500">+${(session?.summary?.cash_sales || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 border-t border-border">
              <span>Efectivo esperado</span>
              <span className="text-primary">${expectedCash.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <Label>Efectivo Contado *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={finalCash}
              onChange={(e) => setFinalCash(e.target.value)}
              placeholder="0.00"
              className="text-xl text-center font-bold"
              autoFocus
            />
          </div>

          {/* Difference */}
          {parseFloat(finalCash) > 0 && (
            <div className={`p-3 rounded-lg text-center ${
              Math.abs(difference) < 0.01 ? 'bg-green-500/10' : 
              difference > 0 ? 'bg-blue-500/10' : 'bg-red-500/10'
            }`}>
              <p className="text-sm">Diferencia</p>
              <p className={`text-2xl font-bold ${
                Math.abs(difference) < 0.01 ? 'text-green-500' : 
                difference > 0 ? 'text-blue-500' : 'text-red-500'
              }`}>
                {difference >= 0 ? '+' : ''}${difference.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">
                {Math.abs(difference) < 0.01 ? 'Cuadra perfecto' :
                 difference > 0 ? 'Sobrante' : 'Faltante'}
              </p>
            </div>
          )}

          <div>
            <Label>Notas (opcional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones al cerrar"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleClose} className="flex-1" disabled={loading}>
              {loading ? 'Cerrando...' : 'Cerrar Caja'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function POSCashSession() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionRes, historyRes] = await Promise.all([
        posAPI.getCurrentSession(),
        posAPI.getSessionHistory({ limit: 10 })
      ]);
      
      setSession(sessionRes.data);
      setHistory(historyRes.data.sessions || []);
    } catch (err) {
      console.error('Error fetching session:', err);
      toast.error('Error cargando datos de caja');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSession = async (data) => {
    try {
      await posAPI.openSession(data);
      toast.success('Caja abierta correctamente');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error abriendo caja');
      throw err;
    }
  };

  const handleCloseSession = async (data) => {
    try {
      const res = await posAPI.closeSession(data);
      toast.success('Caja cerrada correctamente');
      setShowCloseModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cerrando caja');
      throw err;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Control de Caja</h1>
              <p className="text-xs text-muted-foreground">
                {session?.has_open_session ? 'Caja abierta' : 'Caja cerrada'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Current Session Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-6 ${
            session?.has_open_session 
              ? 'bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30' 
              : 'bg-gradient-to-br from-gray-500/20 to-gray-500/5 border border-gray-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              session?.has_open_session ? 'bg-green-500/20' : 'bg-gray-500/20'
            }`}>
              <Wallet className={`w-6 h-6 ${session?.has_open_session ? 'text-green-500' : 'text-gray-500'}`} />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">
                {session?.has_open_session ? 'Caja Abierta' : 'Caja Cerrada'}
              </h2>
              {session?.session && (
                <p className="text-sm text-muted-foreground">
                  Abierta por {session.session.opened_by_name} • {formatDateTime(session.session.opened_at)}
                </p>
              )}
            </div>
          </div>

          {session?.has_open_session && session?.session ? (
            <>
              {/* Session Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-background/50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Banknote className="w-4 h-4" />
                    <span className="text-xs">Inicial</span>
                  </div>
                  <p className="font-bold text-lg">{formatCurrency(session.session.initial_cash)}</p>
                </div>
                <div className="bg-background/50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-xs">Ventas efectivo</span>
                  </div>
                  <p className="font-bold text-lg text-green-500">
                    {formatCurrency(session.session.summary?.cash_sales)}
                  </p>
                </div>
                <div className="bg-background/50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span className="text-xs">Ventas tarjeta</span>
                  </div>
                  <p className="font-bold text-lg text-blue-500">
                    {formatCurrency(session.session.summary?.card_sales)}
                  </p>
                </div>
                <div className="bg-background/50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <span className="text-xs">Esperado</span>
                  </div>
                  <p className="font-bold text-lg text-primary">
                    {formatCurrency(session.session.summary?.expected_cash)}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/pos/sales')}
                >
                  Nueva Venta
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => setShowCloseModal(true)}
                >
                  Cerrar Caja
                </Button>
              </div>
            </>
          ) : (
            <Button
              size="lg"
              className="w-full"
              onClick={() => setShowOpenModal(true)}
            >
              <Wallet className="w-5 h-5 mr-2" />
              Abrir Caja
            </Button>
          )}
        </motion.div>

        {/* History */}
        <div>
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <History className="w-5 h-5" />
            Historial de Cierres
          </h2>
          
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No hay historial de sesiones
            </div>
          ) : (
            <div className="space-y-2">
              {history.filter(s => s.status === 'closed').map((s) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="glass rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">{formatDateTime(s.opened_at)}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.opened_by_name} → {s.closed_by_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{formatCurrency(s.summary?.total_sales)}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.summary?.num_transactions || 0} ventas
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Inicial: </span>
                      <span>{formatCurrency(s.initial_cash)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Final: </span>
                      <span>{formatCurrency(s.final_cash)}</span>
                    </div>
                    <div className={`ml-auto ${
                      Math.abs(s.summary?.difference || 0) < 0.01 ? 'text-green-500' :
                      (s.summary?.difference || 0) > 0 ? 'text-blue-500' : 'text-red-500'
                    }`}>
                      {(s.summary?.difference || 0) >= 0 ? '+' : ''}{formatCurrency(s.summary?.difference)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showOpenModal && (
          <OpenSessionModal
            onClose={() => setShowOpenModal(false)}
            onOpen={handleOpenSession}
          />
        )}
        {showCloseModal && session?.session && (
          <CloseSessionModal
            session={session.session}
            onClose={() => setShowCloseModal(false)}
            onCloseSession={handleCloseSession}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
